import { useLayoutEffect, useState } from "react";
import { Calendar } from "lucide-react";

interface MobileBookingBarProps {
  /** id of the booking widget wrapper to scroll to and watch */
  targetId: string;
}

const HEADER_OFFSET = 88; // fixed header (pt-20) + breathing room

/**
 * Fixed bottom bar for narrow screens (below `lg`) where the booking widget
 * stacks far below the content. Appears after scrolling only while the
 * booking widget is still ahead of the viewport, and jumps to it on tap.
 */
export default function MobileBookingBar({ targetId }: MobileBookingBarProps) {
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    const target = document.getElementById(targetId);
    let frame = 0;
    const update = () => {
      // An intersection alone cannot distinguish "not yet reached" from
      // "already passed" (nor reliably cover a widget taller than the screen).
      setVisible(window.scrollY > 0 && !!target &&
        target.getBoundingClientRect().top > window.innerHeight);
    };
    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };

    // Measure before paint, including on restored non-zero scroll positions.
    update();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    // Observe geometry changes not caused by scrolling, without watching every
    // mutation in the document. Both observers are optional browser features.
    const intersection = target && typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver(scheduleUpdate) : null;
    if (target) intersection?.observe(target);
    const resize = target && typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(scheduleUpdate) : null;
    if (target) resize?.observe(target);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      if (frame) window.cancelAnimationFrame(frame);
      intersection?.disconnect();
      resize?.disconnect();
    };
  }, [targetId]);

  const scrollToWidget = () => {
    const target = document.getElementById(targetId);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
    // Hand focus to the widget so keyboard/AT users land at the destination
    // (the bar hides itself once the widget is on screen).
    const focusable = target.querySelector<HTMLElement>(
      "button, [href], input, select, [tabindex]:not([tabindex='-1'])"
    );
    if (!focusable && !target.hasAttribute("tabindex")) target.tabIndex = -1;
    (focusable ?? target).focus({ preventScroll: true });
  };

  return (
    <div
      className={`lg:hidden fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 ease-out ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      aria-hidden={!visible}
      data-testid="mobile-booking-bar"
    >
      <div
        className="bg-background/95 backdrop-blur border-t border-border shadow-[0_-4px_16px_rgba(0,0,0,0.35)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-center justify-end px-4 py-3" style={{ fontFamily: "Lato" }}>
          <button
            type="button"
            onClick={scrollToWidget}
            tabIndex={visible ? 0 : -1}
            className="shrink-0 inline-flex items-center gap-2 bg-primary hover:bg-accent text-primary-foreground font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"
            data-testid="button-check-availability"
          >
            <Calendar className="h-4 w-4" />
            Check Availability
          </button>
        </div>
      </div>
    </div>
  );
}
